import Skeleton from "react-loading-skeleton";
import OutlookIcon from "@/assets/icons/outlook-icon.svg";
import { ArchiveRestore, Plus, X } from "lucide-react";
import { UpdateAccountFieldName } from "../../types/enums/UpdateAccountField";
import { EntityStatus } from "@repo/types/enums";
import type { LinkedAccountDTO } from "../../types/dtos";
import {
  useGetLinkedAccounts,
  useUpdateLinkedAccount,
  useSettings,
} from "../../hooks/queries";
import { ColorPicker } from "../ui/colorPicker/ColorPicker";
import Button from "../ui/button/Button";

export default function AccountSetup({
  isLinkingNewAccount,
  isNewAccountLinked,
}: {
  isLinkingNewAccount: boolean;
  isNewAccountLinked: boolean;
}) {
  const { data: CoreSettings } = useSettings();
  const { data: linkedAccounts, isPending: loading } = useGetLinkedAccounts();
  const { mutate: updateLinkedAccount } = useUpdateLinkedAccount();

  const handleChangeStatus = (accountId: number, status: EntityStatus) => {
    if (status === EntityStatus.Active) {
      status = EntityStatus.Deleted;
    } else if (status === EntityStatus.Deleted) {
      status = EntityStatus.Active;
    }

    updateLinkedAccount({
      AccountId: accountId,
      Status: status,
      UpdateAccountField: UpdateAccountFieldName.Status,
    });
  };

  const statusGroups =
    linkedAccounts?.reduce<Record<EntityStatus, LinkedAccountDTO[]>>(
      (acc, item) => {
        if (!acc[item.Status]) acc[item.Status] = [];
        acc[item.Status].push(item);
        return acc;
      },
      {} as Record<EntityStatus, LinkedAccountDTO[]>,
    ) || {};

  return (
    <>
      <h1 className="text-lg font-bold mb-2">Account setup</h1>

      {loading && <Skeleton count={3} />}
      {!loading &&
        (Object.entries(statusGroups) as [string, LinkedAccountDTO[]][]).map(
          ([status, accounts]) => {
            const statusEnum = Number(status) as EntityStatus; // ✅ must go here, outside JSX

            return (
              <div key={status} className="mb-5">
                <h2 className="text-sm font-bold mb-1 pb-0">
                  {EntityStatus[statusEnum]} accounts
                </h2>

                {accounts.length > 0 ? (
                  <ul className="mb-2">
                    {accounts.map((account) => (
                      <li
                        key={account.Id}
                        className="flex items-center text-sm text-gray-700 p-2 rounded"
                      >
                        <img
                          src={OutlookIcon}
                          alt="Outlook"
                          width={20}
                          className="block"
                        />

                        <ColorPicker
                          id={account.Id}
                          initialColor={account.BgColorHex}
                          onSave={(id, color) =>
                            updateLinkedAccount({
                              AccountId: Number(id),
                              BgColorHex: color,
                              UpdateAccountField:
                                UpdateAccountFieldName.BgColorHex,
                            })
                          }
                          classNames="ml-3"
                        />

                        <ColorPicker
                          id={account.Id}
                          initialColor={account.TextColorHex}
                          onSave={(id, color) =>
                            updateLinkedAccount({
                              AccountId: Number(id),
                              TextColorHex: color,
                              UpdateAccountField:
                                UpdateAccountFieldName.TextColorHex,
                            })
                          }
                          classNames="ml-3"
                        />

                        <div
                          className="ml-2 rounded p-2"
                          style={{
                            backgroundColor: account.BgColorHex,
                            color: account.TextColorHex,
                          }}
                        >
                          {account.Email}
                        </div>

                        {account.IsSubscriptionExpired && (
                          <a
                            href={`${CoreSettings?.SiteURL}/Auth/ConnectAccount`}
                            rel="noopener noreferrer"
                            className="block ml-5 font-bold hover:bg-gray-100"
                          >
                            Renew
                          </a>
                        )}

                        <Button
                          className="mr-auto ml-5"
                          size="xs"
                          variant="ghost"
                          onClick={() =>
                            handleChangeStatus(account.Id, statusEnum)
                          }
                        >
                          {statusEnum === EntityStatus.Active && (
                            <X size={15} className="hover:text-red-700" />
                          )}

                          {statusEnum === EntityStatus.Deleted && (
                            <ArchiveRestore
                              className="inline-block mr-1 hover:text-green-700"
                              size={15}
                            />
                          )}
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-gray-500">No accounts.</div>
                )}
              </div>
            );
          },
        )}

      {isLinkingNewAccount && !isNewAccountLinked && (
        <div className="text-sm text-red-400">
          An error exists while adding account. Please try after some time.
        </div>
      )}
      <a
        href={`${CoreSettings?.SiteURL}/Auth/ConnectAccount`}
        rel="noopener noreferrer"
        className="mt-2 inline-block rounded py-2 text-sm hover:bg-gray-100"
      >
        <Plus className="inline-block mr-1" size={15} />
        Add account
      </a>
    </>
  );
}
